import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, ArrowRight, MapPin, Clock, DollarSign, CheckCircle, ShoppingBag, FileText, Briefcase, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { AppLayout } from '../../../components/layout/AppLayout';

const SERVICE_CATEGORIES = [
  { value: 'delivery', label: 'Delivery', icon: Package, color: 'from-blue-500 to-blue-600', description: 'Send parcels & packages' },
  { value: 'shopping_assistance', label: 'Shopping', icon: ShoppingBag, color: 'from-green-500 to-green-600', description: 'Help with shopping & purchases' },
  { value: 'document_collection', label: 'Documents', icon: FileText, color: 'from-purple-500 to-purple-600', description: 'Pick up & deliver documents' },
  { value: 'errands', label: 'Errands', icon: Briefcase, color: 'from-orange-500 to-orange-600', description: 'General errands & tasks' },
  { value: 'small_transport', label: 'Small Transport', icon: Truck, color: 'from-red-500 to-red-600', description: 'Move small items' },
];

const RunnersHomePage = () => {
  const navigate = useNavigate();

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Hero Header */}
        <div className="text-center space-y-4 py-8 px-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl mb-4">
            <Package className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Matrise Runners
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Your on-demand errands, delivery and local services marketplace
          </p>
        </div>

        {/* Split-Screen Main Sections */}
        <div className="grid lg:grid-cols-2 gap-0 min-h-[600px] rounded-3xl overflow-hidden shadow-2xl mb-12 mx-4">
          
          {/* LEFT SIDE: Customer (Request Jobs) */}
          <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 p-12 flex flex-col justify-between text-white overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 left-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 space-y-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Package className="h-8 w-8 text-white" />
              </div>
              
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold">
                  I need something done
                </h2>
                <p className="text-xl text-blue-100 leading-relaxed">
                  Get help with deliveries, shopping, errands, document collection, and small transport from trusted runners in your area.
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-4 py-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-200 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Post Your Request</p>
                    <p className="text-sm text-blue-200">Describe what you need and set your budget</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-200 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Get Offers</p>
                    <p className="text-sm text-blue-200">Runners near you will respond with offers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-200 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Track & Complete</p>
                    <p className="text-sm text-blue-200">Chat, track progress, and confirm delivery</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="relative z-10 space-y-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/pickup/post')}
                className="w-full bg-white text-blue-700 hover:bg-blue-50 h-14 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all"
              >
                Request a Job
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/pickup/my-deliveries')}
                className="w-full text-white hover:bg-white/10 h-12 rounded-xl"
              >
                View My Requests
              </Button>
            </div>
          </div>

          {/* RIGHT SIDE: Runner (Earn Money) */}
          <div className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 p-12 flex flex-col justify-between text-white overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 right-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 space-y-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Truck className="h-8 w-8 text-white" />
              </div>
              
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold">
                  I want to earn as a runner
                </h2>
                <p className="text-xl text-green-100 leading-relaxed">
                  Make money on your own schedule. Accept jobs, complete errands, and earn cash helping people in your community.
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-4 py-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-200 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Browse Jobs</p>
                    <p className="text-sm text-green-200">See available jobs near you in real-time</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-200 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Accept & Earn</p>
                    <p className="text-sm text-green-200">Choose jobs that fit your schedule and location</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-200 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-lg">Get Paid Fast</p>
                    <p className="text-sm text-green-200">Instant payment to your wallet upon completion</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="relative z-10 space-y-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/pickup/available')}
                className="w-full bg-white text-green-700 hover:bg-green-50 h-14 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all"
              >
                Browse Available Jobs
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/pickup/my-jobs')}
                  className="text-white hover:bg-white/10 h-12 rounded-xl"
                >
                  My Jobs
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/pickup/driver/earnings')}
                  className="text-white hover:bg-white/10 h-12 rounded-xl"
                >
                  Earnings
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Service Categories Tiles */}
        <div className="px-4 mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">What do you need help with?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {SERVICE_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Card
                  key={category.value}
                  className="p-6 cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1 border-2 hover:border-blue-500 text-center group"
                  onClick={() => navigate(`/pickup/browse?category=${category.value}`)}
                >
                  <div className={`w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-semibold mb-1">{category.label}</h3>
                  <p className="text-xs text-muted-foreground">{category.description}</p>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Quick Access Navigation */}
        <div className="grid md:grid-cols-4 gap-4 px-4 mb-12">
          <button 
            onClick={() => navigate('/pickup/browse')}
            className="bg-white dark:bg-gray-900 p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 transition-all hover:shadow-lg text-left group"
          >
            <MapPin className="h-8 w-8 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg mb-1">Browse Jobs</h3>
            <p className="text-sm text-muted-foreground">See all available jobs</p>
          </button>

          <button 
            onClick={() => navigate('/pickup/find-runner')}
            className="bg-white dark:bg-gray-900 p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-800 hover:border-green-500 dark:hover:border-green-500 transition-all hover:shadow-lg text-left group"
          >
            <Users className="h-8 w-8 text-green-600 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg mb-1">Find a Runner</h3>
            <p className="text-sm text-muted-foreground">Browse verified runners</p>
          </button>

          <button 
            onClick={() => navigate('/pickup/my-jobs')}
            className="bg-white dark:bg-gray-900 p-6 rounded-2xl border-2 border-gray-200 dark:border-gray-800 hover:border-purple-500 dark:hover:border-purple-500 transition-all hover:shadow-lg text-left group"
          >
            <Clock className="h-8 w-8 text-purple-600 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg mb-1">My Jobs</h3>
            <p className="text-sm text-muted-foreground">Posted jobs & jobs I'm doing</p>
          </button>

          <button 
            onClick={() => navigate('/pickup/driver/register')}
            className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 p-6 rounded-2xl border-2 border-purple-300 dark:border-purple-700 hover:border-purple-500 dark:hover:border-purple-500 transition-all hover:shadow-lg text-left group"
          >
            <DollarSign className="h-8 w-8 text-purple-600 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg mb-1 text-purple-600">Become a Runner</h3>
            <p className="text-sm text-muted-foreground">Apply to start earning</p>
          </button>
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-3xl p-12 mx-4 mb-8">
          <h3 className="text-3xl font-bold text-center mb-12">How Matrise Runners Works</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl mb-4">
                <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">1</span>
              </div>
              <h4 className="text-xl font-semibold">Post or Browse</h4>
              <p className="text-muted-foreground">
                Customers post job requests with their budget. Runners browse available jobs nearby.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-2xl mb-4">
                <span className="text-3xl font-bold text-green-600 dark:text-green-400">2</span>
              </div>
              <h4 className="text-xl font-semibold">Connect & Agree</h4>
              <p className="text-muted-foreground">
                Runners make offers or accept the price. Both parties agree on terms via chat.
              </p>
            </div>
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-2xl mb-4">
                <span className="text-3xl font-bold text-purple-600 dark:text-purple-400">3</span>
              </div>
              <h4 className="text-xl font-semibold">Complete & Confirm</h4>
              <p className="text-muted-foreground">
                Runner completes the job. Customer confirms with code. Payment released instantly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default RunnersHomePage;
